/**
 * Centralized Error Tracking Module
 *
 * Provides unified error tracking to both Bugsnag (debugging) and PostHog (analytics)
 * for comprehensive observability and redundancy.
 *
 * Usage:
 *   import { captureError, captureMessage, setUserContext, addBreadcrumb } from '@/lib/errorTracking';
 *
 *   try {
 *     await dangerousOperation();
 *   } catch (error) {
 *     captureError(error, {
 *       userId: user.id,
 *       action: 'dangerousOperation',
 *       metadata: { key: 'value' }
 *     });
 *   }
 */

import Bugsnag from '@bugsnag/js';
import { captureException as posthogCaptureException } from '@/lib/posthog';

// Re-export types for convenience
export type { User } from '@bugsnag/js';

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  route?: string;
  action?: string;
  severity?: ErrorSeverity;
  metadata?: Record<string, unknown>;
}

export type ErrorSeverity = 'info' | 'warning' | 'error';

/**
 * Determines if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Extract meaningful error message from various error types
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    // Handle Supabase PostgrestError
    if (err.message) return String(err.message);
    if (err.msg) return String(err.msg);
    // Try to stringify
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  return String(error);
}

/**
 * Extract structured error details from various error types
 */
function extractErrorDetails(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
    };
  }

  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    return {
      errorMessage: err.message || err.msg,
      errorCode: err.code,
      errorDetails: err.details,
      errorHint: err.hint,
      errorRaw: JSON.stringify(error),
    };
  }

  return {
    errorMessage: String(error),
  };
}

/**
 * Sanitize metadata to remove PII
 * Redacts common PII fields like email, password, ssn, etc.
 */
function sanitizeMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
  if (!metadata) return {};

  const piiFields = ['password', 'ssn', 'social_security', 'credit_card', 'cvv', 'pin'];
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(metadata)) {
    // Redact PII fields
    if (piiFields.some(field => key.toLowerCase().includes(field))) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Redact email addresses in strings
    if (typeof value === 'string') {
      sanitized[key] = value.replace(emailPattern, '[EMAIL]');
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Capture an error to both Bugsnag and PostHog
 *
 * @param error - Error object or unknown error value
 * @param context - Additional context about the error
 *
 * @example
 * captureError(new Error('Something failed'), {
 *   userId: 'user123',
 *   action: 'saveProfile',
 *   metadata: { formData: {...} }
 * });
 */
export function captureError(
  error: Error | unknown,
  context?: ErrorContext
): void {
  if (!isBrowser()) return;

  try {
    const errorDetails = extractErrorDetails(error);
    const sanitizedMetadata = sanitizeMetadata(context?.metadata);

    // Send to Bugsnag
    const bugsnagError = error instanceof Error ? error : new Error(extractErrorMessage(error));

    Bugsnag.notify(bugsnagError, (event) => {
      // Set severity if provided (defaults to 'error' otherwise)
      if (context?.severity) {
        event.severity = context.severity;
      }

      // Set context
      if (context?.action) {
        event.context = context.action;
      } else if (context?.route) {
        event.context = context.route;
      }

      // Set user
      if (context?.userId) {
        event.setUser(context.userId);
      }

      // Add metadata
      if (context?.metadata) {
        event.addMetadata('context', sanitizedMetadata);
      }

      // Add error details for non-Error objects
      if (!(error instanceof Error)) {
        event.addMetadata('error', errorDetails);
      }

      // Add route if available
      if (context?.route) {
        event.addMetadata('navigation', { route: context.route });
      }
    });

    // Send to PostHog
    posthogCaptureException(bugsnagError, {
      source: context?.action || 'unknown',
      route: context?.route,
      userId: context?.userId,
      ...errorDetails,
      ...sanitizedMetadata,
    });
  } catch (trackingError) {
    // Fail silently but log to console
    console.error('[ErrorTracking] Failed to capture error:', trackingError);
  }
}

/**
 * Capture a message (non-error event) to both systems
 *
 * @param message - Message to capture
 * @param severity - Severity level
 * @param context - Additional context
 *
 * @example
 * captureMessage('User completed onboarding', 'info', {
 *   userId: 'user123',
 *   metadata: { completionTime: 300 }
 * });
 */
export function captureMessage(
  message: string,
  severity: ErrorSeverity = 'info',
  context?: ErrorContext
): void {
  if (!isBrowser()) return;

  try {
    const sanitizedMetadata = sanitizeMetadata(context?.metadata);

    // Send to Bugsnag
    Bugsnag.notify(new Error(message), (event) => {
      event.severity = severity;

      if (context?.action) {
        event.context = context.action;
      }

      if (context?.userId) {
        event.setUser(context.userId);
      }

      if (context?.metadata) {
        event.addMetadata('context', sanitizedMetadata);
      }
    });

    // Send to PostHog
    posthogCaptureException(new Error(message), {
      severity,
      source: context?.action || 'message',
      userId: context?.userId,
      ...sanitizedMetadata,
    });
  } catch (trackingError) {
    console.error('[ErrorTracking] Failed to capture message:', trackingError);
  }
}

/**
 * Set user context for error tracking
 * Updates user information in both Bugsnag and PostHog
 *
 * @param user - User object with id, email, name
 *
 * @example
 * setUserContext({
 *   id: 'user123',
 *   email: 'user@example.com',
 *   name: 'John Doe'
 * });
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  name?: string;
} | null): void {
  if (!isBrowser()) return;

  try {
    if (user) {
      // Set in Bugsnag
      Bugsnag.setUser(user.id, user.email, user.name);

      // PostHog user context is set via identifyUser in posthog.ts
      // We don't duplicate it here to avoid circular dependencies
    } else {
      // Clear user context
      Bugsnag.setUser(undefined, undefined, undefined);
    }
  } catch (error) {
    console.error('[ErrorTracking] Failed to set user context:', error);
  }
}

/**
 * Add a breadcrumb to track user actions leading up to an error
 *
 * @param message - Breadcrumb message
 * @param metadata - Additional metadata
 * @param type - Type of breadcrumb (navigation, user, state, etc.)
 *
 * @example
 * addBreadcrumb('User clicked save button', { formValid: true });
 */
export function addBreadcrumb(
  message: string,
  metadata?: Record<string, unknown>,
  type: 'navigation' | 'user' | 'state' | 'error' | 'request' | 'process' = 'user'
): void {
  if (!isBrowser()) return;

  try {
    const sanitizedMetadata = sanitizeMetadata(metadata);

    // Add to Bugsnag
    Bugsnag.leaveBreadcrumb(message, sanitizedMetadata, type);

    // PostHog doesn't have explicit breadcrumbs, but we can track as events
    // This is handled by individual feature tracking
  } catch (error) {
    console.error('[ErrorTracking] Failed to add breadcrumb:', error);
  }
}

/**
 * Start a performance span for tracking operation duration
 * Returns a function to end the span
 *
 * @param name - Name of the operation
 * @param metadata - Additional metadata
 * @returns Function to call when operation completes
 *
 * @example
 * const endSpan = startSpan('generateStrategy', { userId: 'user123' });
 * try {
 *   await generateStrategy();
 *   endSpan();
 * } catch (error) {
 *   endSpan({ failed: true });
 *   throw error;
 * }
 */
export function startSpan(
  name: string,
  metadata?: Record<string, unknown>
): (endMetadata?: Record<string, unknown>) => void {
  if (!isBrowser()) {
    return () => {}; // No-op if not in browser
  }

  const startTime = performance.now();
  const sanitizedMetadata = sanitizeMetadata(metadata);

  addBreadcrumb(`Started: ${name}`, sanitizedMetadata, 'process');

  return (endMetadata?: Record<string, unknown>) => {
    const duration = performance.now() - startTime;
    const sanitizedEndMetadata = sanitizeMetadata(endMetadata);

    addBreadcrumb(`Completed: ${name}`, {
      duration,
      ...sanitizedEndMetadata,
    }, 'process');

    // Report performance to Bugsnag
    Bugsnag.leaveBreadcrumb(`Performance: ${name}`, {
      duration,
      ...sanitizedMetadata,
      ...sanitizedEndMetadata,
    }, 'process');
  };
}

/**
 * Wrap an async function with automatic error capture
 *
 * @param fn - Async function to wrap
 * @param context - Error context to include if function throws
 * @returns Wrapped function
 *
 * @example
 * const safeOperation = withErrorTracking(
 *   async () => { await dangerousOperation(); },
 *   { action: 'dangerousOperation', userId: 'user123' }
 * );
 * await safeOperation();
 */
export function withErrorTracking<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: ErrorContext
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, context);
      throw error;
    }
  }) as T;
}

// Export Bugsnag instance for advanced usage
export { Bugsnag };
